import React, { useEffect, useState } from "react";
import gql from "graphql-tag";
import { RouteComponentProps, navigate } from "@reach/router";
import { useMutation } from "react-apollo";
import queryString from "query-string";
import { useToggle, useStoreAppValue, useRecaptcha } from "@hooks";
import { Bugsnag, RailsUrl } from "@services";
import { LinkType } from "@types";
import { ErrorLoading } from "@organisms";
import { IntentLoadingAnonymous } from "../IntentLoadingAnonymous";
import { IntentLoadingBulkShopView } from "../IntentLoadingBulkShopView";
import IntentLoadingView from "./IntentLoadingView";
import {
  IntentLoadingGiftCardViewMutation,
  IntentLoadingGiftCardViewMutationVariables,
} from "./__generated__/IntentLoadingGiftCardViewMutation";
import {
  IntentLoadingViewMutation as IntentLoadingViewMutationGenerics,
  IntentLoadingViewMutationVariables,
} from "./__generated__/IntentLoadingViewMutation";

// == Types ================================================================

interface IRouteProps {
  runMutation: boolean;
}

type TProps = RouteComponentProps<IRouteProps>;

// == Constants ============================================================
const GIFT_CARD_INTENT_CREATE = gql`
  mutation IntentLoadingGiftCardViewMutation($input: GiftcardIntentCreateInput!) {
    GiftcardIntentCreate(input: $input) {
      ... on MutationError {
        errors {
          input
          inputErrors
        }
      }
      ... on GiftcardIntent {
        id
        token
        ...IntentLoadingViewFragment
      }
    }
  }
  ${IntentLoadingView.fragments}
`;

const INTENT_CREATE_BULK_SHOP = gql`
  mutation IntentLoadingViewMutation($input: IntentCreateInput!) {
    IntentCreate(input: $input) {
      ... on MutationError {
        errors {
          input
          inputErrors
        }
      }
      ... on Intent {
        id
        token
        url
        interstitialUrl
        ...IntentLoadingBulkShopViewFragment
      }
    }
  }
  ${IntentLoadingBulkShopView.fragments}
`;

IntentLoadingViewMutation.defaultProps = {
  runMutation: false,
};

const selectedFundraisersMaker = (fundraiserId) => {
  const selectedFundraisers = JSON.parse(window.localStorage.getItem("selectedFundraisers"));
  window.localStorage.removeItem("selectedFundraisers");
  if (selectedFundraisers && selectedFundraisers.length > 0) {
    return selectedFundraisers;
  }
  if (fundraiserId) {
    return [fundraiserId];
  }
  return [];
};

// == Component ============================================================

export function IntentLoadingViewMutation({ location, runMutation }: TProps) {
  const [giftcardIntent, setGiftcardIntent] = useState();
  const [intent, setIntent] = useState();
  const { currentUser, recaptchaEnterpriseKey } = useStoreAppValue();
  const [showAnonForm, toggleAnonForm] = useToggle(false);
  const [hasError, toggleError] = useToggle(false);
  const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);
  const [giftCardIntentCreateMutation] = useMutation<
    IntentLoadingGiftCardViewMutation,
    IntentLoadingGiftCardViewMutationVariables
  >(GIFT_CARD_INTENT_CREATE);
  const [intentCreateMutation] = useMutation<
    IntentLoadingViewMutationGenerics,
    IntentLoadingViewMutationVariables
  >(INTENT_CREATE_BULK_SHOP);
  const recaptchaAction = currentUser ? "CREATE_GIFTCARD_INTENT" : "CREATE_ANONYMOUS_GIFTCARD_INTENT";
  const getRecaptchaToken = useRecaptcha({ action: recaptchaAction });

  const createGiftcardIntent = async() => {
    const {
      amountCents,
      deviceAttributes,
      fundraiserId,
      isGift,
      isPhysical,
      linkId,
      quantity,
      reloadThresholdCents,
      promotionId,
    } = queryString.parse(location.search);

    try {
      const token = await getRecaptchaToken();

      const input = {
        fundraiserId,
        fundraiserIds: selectedFundraisersMaker(fundraiserId),
        amountCents: parseInt(amountCents, 10),
        deviceAttributes: JSON.parse(deviceAttributes),
        isGift: isGift === "true",
        isPhysical: isPhysical === "true",
        linkId,
        quantity: parseInt(quantity, 10),
        promotionId,
        recaptchaToken: token
      };
      if (reloadThresholdCents) {
        input.reloadThresholdCents = parseInt(reloadThresholdCents, 10);
      }

      giftCardIntentCreateMutation({
        variables: {
          input,
        },
      })
      .then(({ data }) => {
        const { GiftcardIntentCreate } = data;
        if (!GiftcardIntentCreate || GiftcardIntentCreate.__typename === "MutationError") {
          if (GiftcardIntentCreate.errors?.[0]?.input === "quantity") {
            setErrorMessage(
              "We're currently sold out of this gift card. We've been notified and will re-stock it shortly, sorry for any inconvenience."
            );
          }
          toggleError();
          return;
        }
        const { token: intentToken } = GiftcardIntentCreate;
        navigate(RailsUrl.intentUrl({ intentToken }), {
          replace: true,
          state: { giftcardIntent: GiftcardIntentCreate },
        });
      })
      .catch((e) => {
        toggleError(true);
        Bugsnag.notify(e);
      });
    } catch (e) {
      toggleError(true);
      Bugsnag.notify(e);
    }
  };

  const createShopIntent = async() => {
    const { deviceAttributes, fundraiserId, linkId, promotionId } = queryString.parse(
      location.search
    );
    try {
      intentCreateMutation({
        variables: {
          input: {
            fundraiserId,
            fundraiserIds: selectedFundraisersMaker(fundraiserId),
            deviceAttributes: JSON.parse(deviceAttributes),
            linkId,
            promotionId
          },
        },
      })
      .then(({ data }) => {
        const { IntentCreate } = data;
        const { interstitialUrl } = IntentCreate;
        if (!IntentCreate || IntentCreate.errors) {
          toggleError();
        } else {
          setIntent(IntentCreate);
          window.location.replace(interstitialUrl);
        }
      })
      .catch((e) => {
        toggleError(true);
        Bugsnag.notify(e);
      });

    } catch (e) {
      toggleError(true);
      Bugsnag.notify(e);
    }
  };

  useEffect(() => {
    const { linkType } = queryString.parse(location.search);
    if (runMutation) {
      if (
        linkType === LinkType.GIFTCARDOFFER ||
        linkType === LinkType.AUTORELOADGIFTCARD ||
        linkType === LinkType.VOUCHER
      ) {
        createGiftcardIntent();
      } else {
        createShopIntent();
      }
    } else {
      // this scenario occurs because the user is navigating here after creating the giftcard mutation
      // a page reload occurs, so we have to set the state
      if (!currentUser) {
        toggleAnonForm();
      }

      if (location.state?.giftcardIntent) {
        setGiftcardIntent(location.state.giftcardIntent);
        if (currentUser) {
          setTimeout(() => {
            navigate(RailsUrl.checkoutUrl(location.state.giftcardIntent.token), { replace: true });
          }, 3000);
        }
      }

      // this if statement shouldn't happen because anon shops shouldn't go through his path
      if (location.state?.intent) {
        setIntent(location.state.intent);
      }

      if (!location.state?.giftcardIntent && !location.state?.intent) {
        window.location.href = location.href;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (hasError)
    return (
      <ErrorLoading ctaLabel={errorMessage ? "Back To Store" : undefined} message={errorMessage} />
    );

  if (showAnonForm && giftcardIntent) {
    return (
      <IntentLoadingAnonymous
        giftcardIntent={giftcardIntent}
        location={location}
        toggleAnonForm={toggleAnonForm}
        toggleError={toggleError}
      />
    );
  }

  if (showAnonForm && intent) {
    return (
      <IntentLoadingAnonymous
        giftcardIntent={intent}
        location={location}
        toggleAnonForm={toggleAnonForm}
        toggleError={toggleError}
      />
    );
  }

  if (giftcardIntent) return <IntentLoadingView giftcardIntent={giftcardIntent} />;

  return <IntentLoadingBulkShopView intent={intent} />;
}
// == Styles ===============================================================
